import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

/**
 * IllustrationWrapper - A reusable component for displaying illustrations with title and subtitle
 * 
 * @param {React.ReactNode} illustration - The illustration component (SVG component)
 * @param {string} title - Main title text (optional)
 * @param {string} subtitle - Subtitle/description text (optional)
 * @param {object} illustrationProps - Props to pass to the illustration component
 * @param {string} titleVariant - MUI Typography variant for title (default: 'h5')
 * @param {string} subtitleVariant - MUI Typography variant for subtitle (default: 'body1')
 * @param {string} alignment - Text alignment: 'left', 'center', 'right' (default: 'center')
 * @param {number|string} spacing - Spacing between elements (default: 2)
 * @param {object} titleStyle - Additional styles for title
 * @param {object} subtitleStyle - Additional styles for subtitle
 * @param {object} containerStyle - Additional styles for main container
 * @param {string} className - Additional CSS class names for container
 * @param {boolean} reverseOrder - If true, shows text above illustration (default: false)
 * @param {object} ...props - Additional props passed to the main Box container
 */
const IllustrationWrapper = ({
  illustration,
  title,
  subtitle,
  illustrationProps = {},
  titleVariant = 'h2',
  subtitleVariant = 'p1',
  alignment = 'center',
  spacing = 2,
  titleStyle = {},
  subtitleStyle = {},
  containerStyle = {},
  className = '',
  reverseOrder = false,
  ...props
}) => {
  const theme = useTheme();

  // Default styles based on theme
  const defaultTitleStyle = {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginBottom: title && subtitle ? theme.spacing(1) : 0,
    ...titleStyle
  };

  const defaultSubtitleStyle = {
    color: theme.palette.text.secondary,
    ...subtitleStyle
  };

  const defaultContainerStyle = {
    display: 'flex',
    flexDirection: reverseOrder ? 'column-reverse' : 'column',
    alignItems: alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center',
    textAlign: alignment,
    gap: theme.spacing(spacing),
    ...containerStyle
  };

  const renderText = () => {
    if (!title && !subtitle) return null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'inherit', width: '100%' }}>
        {title && (
          <Typography 
            variant={titleVariant} 
            sx={defaultTitleStyle}
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography 
            variant={subtitleVariant} 
            sx={defaultSubtitleStyle}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    );
  };

  const renderIllustration = () => {
    if (!illustration) return null;

    // Clone the illustration with additional props
    return React.cloneElement(illustration, illustrationProps);
  };

  return (
    <Box 
      className={className}
      sx={defaultContainerStyle}
      {...props}
    >
      {renderIllustration()}
      {renderText()}
    </Box>
  );
};

export default IllustrationWrapper;